import type { Ring, Vec2 } from '../../../../geo/types';
import { ringPoints } from '../../solids';

/**
 * The Sint Antoniespoort as it stood in 1538 (docs/plans/landmarks.md, "Sint Antoniespoort").
 *
 * Plan: the masonry of today's Waag keeps the gate's form and size of 1538 (De Graauw, Bulletin
 * KNOB 2011-3/4, pp. 124-125, from the painting), so the plan is OpenStreetMap way 57857390, measured
 * in the frame below: u from the city toward the field along the passage, v to its left. AAR 95
 * (2017) names and places the parts (p. 11; sporenkaart fig. 18, p. 28, registered on the six tower
 * centres at 19 px/m): the hoofdpoort with the Sint-Eloystoren (E) and the Schutterstoren (S) standing
 * in the moat and the stair towers Sint-Lucastoren (L) and Metselaarstoren (M) on the city side; the
 * vaulted culvert under the passage (p. 32); the voorpoort beyond it with the Klompenmakerstoren (K)
 * and the Theatrum Anatomicumtoren (TA) on its corners.
 *
 * Heights, above the ground of 1538: Glaudemans's reconstruction of the gate before 1617, seen from
 * the east (AAR 95 fig. 5, p. 12), at 39.5 px/m from S's OSM diameter, with the top band of stone at
 * 5.8 m: 5.5 m above today's ground (De Graauw, p. 121), which lies some tens of centimetres above the
 * old one (p. 123). Rows below are that drawing's.
 */
/**
 * The gate as painted (painting scan): its walls a warm dark ochre, #6a561f (Lab 37.4 1.6 33.7) on
 * the hoofdpoort's west flank (1890-1935 x 3010-3080); its spires darker and olive, #4d5229 (Lab
 * 32.8 -7.2 22.3) over TA's spire and S's roof (1955-1972 x 2960-2985, 2000-2020 x 2960-2990). The
 * brick and slate centres nearest them draw it (fortificationFeature.ts).
 */
export const PAINTED_GATE = { wall: '#6a561f', roof: '#4d5229' } as const;

export const ANTONIESPOORT_FRAME = { origin: [513.7, -31.2] as Vec2, heading: -1.117 } as const;

/* Every part stands in the moat or on its bank, founded below the water's bottom. */
export const FOOT = -1.2;

export const HOOFDPOORT = {
  u: [-1.77, 14.6] as const,
  v: [0.04, 14.05] as const,
  /** Crenel bottoms (row 895) and merlon tops (row 858). */
  walk: 9.6,
  merlonTop: 10.55,
  /** Merlons 1.05 m wide at 1.45 m, the crenels narrow slots (fig. 5, x 755-1065). */
  merlons: { width: 1.05, spacing: 1.45 },
  /** Top of the walls banded with stone; plain brick above (De Graauw, p. 121; fig. 5, row 1045). */
  bandsTo: 5.8,
  /**
   * Gable to the city, hip to the field (fig. 5: ridge row 497 from x 690 to the gable at x 1060).
   * The eaves stand behind the merlons, 0.5 m in, so the ridge ends at u 7.6 where fig. 5 ends it at 7.7.
   */
  roof: { u: [-1.77, 14.1] as const, v: [0.54, 13.55] as const, ridge: 19.7 },
} as const;

/** The passage: 3.23 m between its jambs (AAR 95 p. 39), on the axis of fig. 18; its arch is not measured. */
export const PASSAGE = { v: [5.185, 8.415] as const, springing: 3.4 } as const;

export interface RoundTower {
  readonly name: string;
  readonly centre: Vec2;
  readonly radius: number;
  /** Plan angle the tower's outer side turns to, counter-clockwise from u. */
  readonly facing: number;
  /** Turns its windows as drawn (+1) or mirrored across the passage axis (-1). */
  readonly hand: 1 | -1;
}

/** E and S, heightened in the 1500s (dendro 1503, AAR 95 p. 26); fig. 5 draws S, E is its mirror. */
export const BIG_TOWERS: readonly RoundTower[] = [
  { name: 'Sint-Eloystoren', centre: [11.1, -0.67], radius: 3.46, facing: -Math.PI / 2, hand: -1 },
  { name: 'Schutterstoren', centre: [11.09, 15.07], radius: 3.49, facing: Math.PI / 2, hand: 1 },
];

const DEG = Math.PI / 180;

export const BIG_TOWER = {
  segments: 24,
  /** Eaves row 810, apex row 520, vane top row 340. */
  eave: 11.75,
  apex: 19.1,
  finialTop: 23.5,
  /** The moulded eave fig. 5 draws as a double line (rows 805-816), the painting's white line under the roof. */
  cornice: { depth: 0.35, reach: 0.15 },
  /**
   * The bell roof as fig. 5 draws S's (x 400-690): ring radii as shares of the tower's, read every 1 m up
   * the curve from the eaves (row 812, r 3.64 m) to where it runs straight to the apex (row 640).
   * Visscher's drawing of shortly after 1601 (AAR 95 fig. 9) flares it the same way before S's roof of 1606.
   */
  roofProfile: [
    [11.75, 1.043],
    [12.5, 0.877],
    [13.5, 0.693],
    [14.5, 0.553],
    [15.5, 0.43],
  ],
  /**
   * Dormers: fig. 5 draws one to the front with its window (x 540-575, rows 632-690) and two in profile
   * with spikes to row 620; Visscher draws S's with two spiked ones beside the front one. Four, a
   * quarter turn apart, the window only in the front one.
   */
  dormer: {
    sill: 14.8,
    eave: 15.7,
    peak: 16.25,
    spikeTop: 16.6,
    width: 0.8,
    out: 0.1,
    window: 0.3,
  },
  /** Three arched windows (sill row 1005, springing row 933) at their angles from the facing, toward the city. */
  windows: { sill: 6.86, head: 8.64, width: 0.9, at: [-42.6 * DEG, 15.3 * DEG, 60 * DEG] },
  /** Their hood moulds, the dashed arcs 0.15 m over the heads (rows 908-918). */
  surround: 0.15,
  /** Four small windows under the eaves (rows 822-852). */
  smallWindows: {
    sill: 10.68,
    head: 11.44,
    width: 0.5,
    at: [-58 * DEG, 15 * DEG, 37 * DEG, 59 * DEG],
  },
} as const;

/** The voorpoort's corner towers, round as their foundations (AAR 95 fig. 18). */
export const VOORPOORT_TOWERS: readonly RoundTower[] = [
  {
    name: 'Klompenmakerstoren',
    centre: [20.78, 0.99],
    radius: 1.74,
    facing: -Math.PI / 4,
    hand: -1,
  },
  {
    name: 'Theatrum Anatomicumtoren',
    centre: [20.74, 13.42],
    radius: 1.74,
    facing: Math.PI / 4,
    hand: 1,
  },
];

/**
 * An octagonal sandstone plinth under a round body banded with Ledesteen (AAR 95 p. 30, fig. 21; the
 * plinths top out at +0.05 and +0.40 m NAP, at the water), round up to the gallery as TA stands today
 * (the user's photograph) and as Visscher draws it (AAR 95 fig. 9, x 180-280). The gallery projects on
 * a corbel frieze (fig. 5 rows 895-935), its parapet (rows 875-895) carries four pinnacles (rows
 * 855-880) round an octagonal spire (rows 625-875) and a vane (row 520). Slits (rows 957-987) on the
 * faces fig. 5 shows.
 */
export const VOORPOORT_TOWER = {
  segments: 16,
  plinth: { top: -0.2, proud: 0.08 },
  /**
   * Top of the round body's Ledesteen bands (AAR 95 pp. 30-31): fig. 5 draws K's banded lower part
   * up to a set-back at rows 1058-1066, 5.3 to 5.5 m, just under the crenels of the walls beside it.
   */
  bandsTo: 5.4,
  corbels: { from: 8.6, to: 9.0, count: 16, width: 0.22 },
  gallery: { from: 9.0, to: 9.6, reach: 0.3 },
  parapet: { to: 10.1, thickness: 0.2 },
  pinnacles: { top: 10.8, radius: 0.1 },
  apex: 16.4,
  finialTop: 19.1,
  slits: { sill: 7.3, head: 8.0, width: 0.25, at: [0, Math.PI / 4] },
} as const;

/** The Sint-Lucastoren, octagonal as today (AAR 95 p. 45); fig. 5 draws its spire and skirt (rows 502-795). */
export const SINT_LUCASTOREN = {
  ring: ringPoints([-1.99, -0.07], 1.74, 8, -21 * DEG),
  eave: 12.1,
  cornice: { depth: 0.3, reach: 0.12 },
  apex: 17.7,
  kick: { riseShare: 0.125, scale: 0.74 },
  finialTop: 19.5,
} as const;

/*
 * The Metselaarstoren's forerunner, smaller than the octagonal tower of 1617 and not heightened with
 * the rest (De Graauw, p. 123): its square foot (AAR 95 p. 44) at the height of the early gate, about
 * 8 m (De Graauw, p. 123). Its size is not given.
 */
export const METSELAARSTOREN = { centre: [-2.04, 13.81] as Vec2, half: 1.2, eave: 8.0, apex: 10.5 };

/** The walls either side of the passage over the culvert (AAR 95 p. 11), on today's walls there. */
export const CULVERT_WALLS = {
  u: [14.6, 18.73] as const,
  v: [
    [1.0, 1.8],
    [12.61, 13.41],
  ] as const,
  /** Crenel bottoms row 1057, merlon tops row 1032; merlons 0.95 m wide at 1.25 m (fig. 5, x 222-405). */
  walk: 5.5,
  merlonTop: 6.15,
  merlons: { width: 0.95, spacing: 1.25 },
} as const;

/**
 * The voorpoort between its towers, at the height of the walls over the culvert. Its arch is not
 * measured: as wide as the hoofdpoort's, its crown 1 m below the crenels.
 */
export const VOORPOORT = {
  u: [18.73, 20.73] as const,
  v: [1.0, 13.41] as const,
  springing: 2.885,
} as const;

export const MERLON_DEPTH = 0.5;

/**
 * Arched corbel friezes (fig. 5): under the hoofdpoort's parapet on its flanks (rows 928-940, arches
 * about 0.5 m) and under the merlons of the walls over the culvert (rows 1080-1095). A band on small
 * corbels, in the walls' brick; the field front's is taken to match the flanks (L).
 */
export const FRIEZES = {
  hoofdpoort: { corbels: 8.45, band: 8.6, top: 8.8, reach: 0.2 },
  culvert: { corbels: 4.5, band: 4.7, top: 4.9, reach: 0.15 },
  spacing: 0.5,
  corbelWidth: 0.15,
} as const;

/*
 * The culvert's mouths in the walls over it (fig. 5, x 180-360, the arch nearly as long as the wall).
 * The road runs over its crown, so here, with the water 0.8 m under the ground, only its top shows.
 */
export const CULVERT_MOUTH = { u: [14.9, 18.43] as const, sill: -1.0, head: -0.25 } as const;

/**
 * The city front's windows after Visscher (AAR 95 fig. 9, x 700-815, rows 215-340): two lancets in
 * the gable and a pair either side of the passage lower down. Drawn some sixty years after 1538 (L).
 */
export const CITY_WINDOWS = [
  { v: 6.4, sill: 12.8, head: 13.8, width: 0.4 },
  { v: 7.7, sill: 12.8, head: 13.8, width: 0.4 },
  { v: 3.0, sill: 6.8, head: 7.6, width: 0.5 },
  { v: 3.7, sill: 6.8, head: 7.6, width: 0.5 },
  { v: 10.4, sill: 6.8, head: 7.6, width: 0.5 },
  { v: 11.1, sill: 6.8, head: 7.6, width: 0.5 },
] as const;

/**
 * The hoofdpoort's city end: a stepped gable standing above its roof (Visscher, "d'Oude S Antonis
 * poort", c. 1601, AAR 95 fig. 9, x 700-775, rows 160-265), in the wall's brick, as thick as a
 * parapet. Visscher draws the steps too small to count: six a side, each 0.45 m above the roof line
 * at its inner edge, and a crown 0.9 m over the ridge, are chosen within his drawing (L).
 */
export const CITY_GABLE = { thickness: 0.5, steps: 6, coping: 0.45, crown: 0.9 } as const;

/**
 * Vanes on the spires of S, TA and L, as Visscher draws them (AAR 95 fig. 9: x 265-290 rows 250-300
 * on S's dormer, x 90-105 rows 195-250 on TA, x 375-395 rows 50-100 on S): a flag on the rod, its
 * top this far below the rod's top. Their sizes are not recorded (L).
 */
export const VANE = { below: 0.5, height: 0.45, length: 0.8, thickness: 0.03 } as const;

/** Chimneys rising from the east eaves and the east slope (fig. 5: x 940-975, 805-875, 705-740). */
export const CHIMNEYS = [
  { u: [0.5, 1.4] as const, v: [10.4, 11.0] as const, top: 20.5 },
  { u: [3.03, 4.83] as const, v: [12.95, 13.55] as const, top: 16.6 },
  { u: [6.45, 7.35] as const, v: [12.95, 13.55] as const, top: 14.0 },
] as const;

export function rectRing(u: readonly [number, number], v: readonly [number, number]): Ring {
  return [
    [u[0], v[0]],
    [u[1], v[0]],
    [u[1], v[1]],
    [u[0], v[1]],
  ];
}
